import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { PageHeader } from "@/components/PageHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowDown, ArrowUp, ArrowUpDown, Download, FileSpreadsheet,
  FileText, Filter,
} from "lucide-react";

import {
  TagAssignmentPopover,
  TagFilter,
  TagList,
  useEntityTagsPolymorphic,
} from "@/components/TagComponents";
import {
  DocumentBadge,
  useDocumentTypes,
} from "@/components/DocumentComponents";

import { listPrimaNota, primaNotaSummary, listCategories, listVendors } from "@/lib/api";
import { arListClients, arListCategories } from "@/lib/ar-api";
import { EUR, fmtDate, paymentMethodIT, todayISO, yearBounds } from "@/lib/format";
import { exportRows } from "@/lib/export";

const ORIGIN_LABEL = {
  spesa_ordinaria: "Spesa ordinaria",
  spesa_ricorrente: "Spesa ricorrente",
  piano_rateale: "Piano rateale",
  incasso_ordinario: "Incasso ordinario",
  incasso_ricorrente: "Incasso ricorrente",
  incasso_giornaliero: "Incasso giornaliero",
};

const ORIGIN_TONE = {
  spesa_ordinaria: "bg-stone-100 text-stone-700 border-stone-200",
  spesa_ricorrente: "bg-rose-50 text-rose-700 border-rose-200",
  piano_rateale: "bg-amber-50 text-amber-800 border-amber-200",
  incasso_ordinario: "bg-emerald-50 text-emerald-700 border-emerald-200",
  incasso_ricorrente: "bg-teal-50 text-teal-700 border-teal-200",
  incasso_giornaliero: "bg-sky-50 text-sky-700 border-sky-200",
};

const METHOD_OPTIONS = [
  { value: "bank_transfer", label: "Banca / Bonifico" },
  { value: "cash", label: "Contanti" },
  { value: "card", label: "POS / Carta" },
  { value: "sdd", label: "RID-SDD" },
  { value: "check", label: "Assegno" },
  { value: "direct_debit", label: "Addebito" },
  { value: "other", label: "Altro" },
];

export default function PrimaNota() {
  const [yFrom, yTo] = yearBounds();
  const [searchParams] = useSearchParams();
  // Initial filters can be seeded via URL (deep-link da Cruscotto / altre pagine).
  // Solo le chiavi note vengono accettate; in caso di mancanza si applicano
  // i default annuali.
  const initFromUrl = (key, fallback) => {
    const v = searchParams.get(key);
    return v !== null && v !== "" ? v : fallback;
  };
  const [filters, setFilters] = useState({
    date_from: initFromUrl("date_from", yFrom),
    date_to: initFromUrl("date_to", yTo),
    tipo: initFromUrl("tipo", "all"),
    category_id: initFromUrl("category_id", "all"),
    subject_id: initFromUrl("subject_id", "all"),
    method: initFromUrl("method", "all"),
    origin: initFromUrl("origin", "all"),
    q: initFromUrl("q", ""),
    partial_only: searchParams.get("partial_only") === "true",
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    total_in: 0,
    total_out: 0,
    net: 0,
    count: 0,
  });
  const [loading, setLoading] = useState(false);

  // Saldo progressivo (P1 — Iter35)
  // Persistito in localStorage perché è una preferenza utente operativa
  // (single-tenant studio): saldo iniziale + flag visualizzazione.
  const [showSaldo, setShowSaldo] = useState(
    () => localStorage.getItem("pn-show-saldo") === "1",
  );
  const [saldoInitial, setSaldoInitial] = useState(
    () => localStorage.getItem("pn-saldo-initial") || "",
  );
  useEffect(() => {
    localStorage.setItem("pn-show-saldo", showSaldo ? "1" : "0");
  }, [showSaldo]);
  useEffect(() => {
    localStorage.setItem("pn-saldo-initial", saldoInitial);
  }, [saldoInitial]);

  // Deep-link from Scadenze: filtra Prima Nota su una specifica occurrence.
  // Mostriamo una banner contestuale quando attivo.
  const [occurrenceFilter, setOccurrenceFilter] = useState(
    () => searchParams.get("occurrence_id") || "",
  );

  // Lookup data
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [categoriesAp, setCategoriesAp] = useState([]);
  const [categoriesAr, setCategoriesAr] = useState([]);

  // Sort
  const [sort, setSort] = useState({ field: "date", dir: "desc" });

  useEffect(() => {
    (async () => {
      try {
        const [v, c, capt, car] = await Promise.all([
          listVendors(),
          arListClients(),
          listCategories(),
          arListCategories(),
        ]);
        setVendors(v || []);
        setClients(c || []);
        setCategoriesAp(capt || []);
        setCategoriesAr(car || []);
      } catch {
        // Non-blocking
      }
    })();
  }, []);

  // Debounce la ricerca testuale (350ms). Motivazione: `filters.q` è
  // in `useEffect` deps, quindi senza debounce ogni carattere digitato
  // fa partire 2 chiamate API (listPrimaNota + primaNotaSummary).
  // Il campo `<Input>` resta controllato dallo state completo, ma il
  // fetch viene attivato solo dopo l'ultima keystroke stabile.
  const debouncedQ = useDebouncedValue(filters.q, 350);

  const buildParams = () => {
    const p = {
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      tipo: filters.tipo,
      q: debouncedQ || undefined,
      partial_only: filters.partial_only || undefined,
      sort: sort.field,
      direction: sort.dir,
    };
    if (filters.category_id && filters.category_id !== "all")
      p.category_id = filters.category_id;
    if (filters.subject_id && filters.subject_id !== "all")
      p.subject_id = filters.subject_id;
    if (filters.method && filters.method !== "all") p.method = filters.method;
    if (filters.origin && filters.origin !== "all") p.origin = filters.origin;
    if (occurrenceFilter) p.occurrence_id = occurrenceFilter;
    return p;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = buildParams();
        const [list, s] = await Promise.all([
          listPrimaNota(params),
          primaNotaSummary(params),
        ]);
        if (cancelled) return;
        setRows(list || []);
        setSummary(s || { total_in: 0, total_out: 0, net: 0, count: 0 });
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, "Errore caricamento Prima Nota"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    filters.date_from, filters.date_to, filters.tipo, filters.category_id,
    filters.subject_id, filters.method, filters.origin, debouncedQ,
    filters.partial_only, sort.field, sort.dir, occurrenceFilter,
  ]);

  // v1.59 ck14.2: mappa tipi documento (id → obj) + conteggio allegati per riga.
  // I types sono cachati globalmente via useDocumentTypes.
  const documentTypes = useDocumentTypes(true); // include inactive per read storici
  const documentTypesById = useMemo(() => {
    const m = {};
    for (const t of documentTypes) m[t.id] = t;
    return m;
  }, [documentTypes]);
  // Attachment count sarà popolato in una fase successiva se necessario.
  // Per ora è vuoto: badge senza icona paperclip nella prima UI.
  const attachmentsCountByRow = useMemo(() => ({}), []);

  // Etichette (Fase 2 — polimorfico Prima Nota):
  // - kind=payment       → tag su `occurrence`            (occurrence_id)
  //     ↳ etichetta condivisa con Scadenze spese
  // - kind=collection    → tag su `receivable_occurrence` (occurrence_id)
  //     ↳ etichetta condivisa con Scadenze incassi
  // - kind=daily_receipt → tag su `daily_receipt`         (dr.id, prefix stripped)
  //     ↳ etichetta autonoma, condivisa con Incassi ordinari
  const [tagFilter, setTagFilter] = useState({ selectedIds: [], mode: "or", untaggedOnly: false });

  const resolveEntity = useCallback((row) => {
    if (row.kind === "payment") {
      if (!row.occurrence_id) return null;
      return { entityType: "occurrence", entityId: row.occurrence_id, rowKey: row.id };
    }
    if (row.kind === "collection") {
      if (!row.occurrence_id) return null;
      return { entityType: "receivable_occurrence", entityId: row.occurrence_id, rowKey: row.id };
    }
    if (row.kind === "daily_receipt") {
      // row.id ha il prefisso "dr-" → estrai UUID reale
      const drId = String(row.id || "").replace(/^dr-/, "");
      if (!drId) return null;
      return { entityType: "daily_receipt", entityId: drId, rowKey: row.id };
    }
    return null;
  }, []);

  const { tagsByEntity, refetch: refetchTags, resolveByRowKey } =
    useEntityTagsPolymorphic(rows, resolveEntity);

  // Rows dopo filtro tag (post-fetch client-side, coerente col resto del
  // codebase Etichette Fase 1/2). Ordine di priorità:
  // 1. untaggedOnly (mutuamente esclusivo con selectedIds) → 0 tag
  // 2. selectedIds con OR/AND
  const rowsFiltered = useMemo(() => {
    let base = rows;
    // v1.59 ck14.2: filtri Tipo documento + presenza documento
    if (filters.doc_type_id) {
      base = base.filter((r) => r.doc_type_id === filters.doc_type_id);
    }
    if (filters.doc_presence === "with") {
      base = base.filter((r) => r.doc_type_id || r.doc_number || r.invoice_reference);
    } else if (filters.doc_presence === "without") {
      base = base.filter((r) => !r.doc_type_id && !r.doc_number && !r.invoice_reference);
    }
    if (tagFilter.untaggedOnly) {
      return base.filter((r) => {
        // Solo righe che possono avere tag (resolveEntity != null) e
        // che effettivamente non ne hanno. Le righe senza mapping tag
        // vengono comunque incluse (non hanno etichette per definizione).
        return (tagsByEntity[r.id] || []).length === 0;
      });
    }
    if (tagFilter.selectedIds.length === 0) return base;
    return base.filter((r) => {
      const rowTagIds = new Set((tagsByEntity[r.id] || []).map((t) => t.id));
      if (tagFilter.mode === "and") {
        return tagFilter.selectedIds.every((id) => rowTagIds.has(id));
      }
      return tagFilter.selectedIds.some((id) => rowTagIds.has(id));
    });
  }, [rows, tagFilter, tagsByEntity, filters.doc_type_id, filters.doc_presence]);

  // Mappa id-riga → saldo progressivo. Calcolata SEMPRE in ordine cronologico
  // (asc, tie-break per id stabile) e poi proiettata sulla riga corrente
  // a prescindere dal sort selezionato dall'utente. In questo modo il valore
  // del saldo resta coerente con la realtà temporale del periodo.
  const saldoByRowId = useMemo(() => {
    if (!showSaldo) return null;
    const initial = Number(saldoInitial) || 0;
    const chrono = [...rowsFiltered].sort((a, b) => {
      if (a.date < b.date) return -1;
      if (a.date > b.date) return 1;
      return (a.id || "").localeCompare(b.id || "");
    });
    const map = {};
    let acc = initial;
    for (const r of chrono) {
      acc += Number(r.amount_in || 0) - Number(r.amount_out || 0);
      map[r.id] = acc;
    }
    return map;
  }, [rowsFiltered, showSaldo, saldoInitial]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  // Soggetto = unione vendors + clients (qualificati). Mostrati con prefisso.
  const subjectOptions = useMemo(() => {
    if (filters.tipo === "out") {
      return (vendors || []).map((v) => ({ id: v.id, label: v.name, kind: "vendor" }));
    }
    if (filters.tipo === "in") {
      return (clients || []).map((c) => ({ id: c.id, label: c.name, kind: "client" }));
    }
    const merged = [
      ...((vendors || []).map((v) => ({ id: v.id, label: `🏷 ${v.name}`, kind: "vendor" }))),
      ...((clients || []).map((c) => ({ id: c.id, label: `👤 ${c.name}`, kind: "client" }))),
    ];
    return merged;
  }, [vendors, clients, filters.tipo]);

  const categoryOptions = useMemo(() => {
    if (filters.tipo === "out") return categoriesAp;
    if (filters.tipo === "in") return categoriesAr;
    return [...categoriesAp, ...categoriesAr];
  }, [categoriesAp, categoriesAr, filters.tipo]);

  const toggleSort = (field) => {
    setSort((s) =>
      s.field === field
        ? { field, dir: s.dir === "asc" ? "desc" : "asc" }
        : { field, dir: field === "date" || field === "amount" ? "desc" : "asc" },
    );
  };

  const sortIcon = (field) => {
    if (sort.field !== field)
      return <ArrowUpDown size={12} className="ml-1 inline opacity-40" />;
    return sort.dir === "asc" ? (
      <ArrowUp size={12} className="ml-1 inline" />
    ) : (
      <ArrowDown size={12} className="ml-1 inline" />
    );
  };

  /* -------- Exports -------- */
  const columns = [
    { key: "date", header: "Data", type: "date" },
    {
      key: "type",
      header: "Tipo",
      accessor: (r) => (r.type === "in" ? "Entrata" : "Uscita"),
      type: "string",
    },
    { key: "description", header: "Descrizione", type: "string" },
    { key: "notes", header: "Note", type: "string" },
    { key: "invoice_reference", header: "Documento", type: "string" },
    { key: "invoice_date", header: "Data fattura", type: "date" },
    { key: "category_name", header: "Categoria", type: "string" },
    { key: "subject_name", header: "Soggetto", type: "string" },
    {
      key: "amount_in",
      header: "Entrata",
      accessor: (r) => (r.amount_in != null ? r.amount_in : null),
      type: "currency",
      decimals: 2,
    },
    {
      key: "amount_out",
      header: "Uscita",
      accessor: (r) => (r.amount_out != null ? r.amount_out : null),
      type: "currency",
      decimals: 2,
    },
    {
      key: "method",
      header: "Metodo",
      accessor: (r) => (r.method ? paymentMethodIT[r.method] || r.method : ""),
      type: "string",
    },
    {
      key: "origin",
      header: "Origine",
      accessor: (r) => ORIGIN_LABEL[r.origin] || r.origin,
      type: "string",
    },
    ...(showSaldo
      ? [{
          key: "saldo",
          header: "Saldo",
          accessor: (r) =>
            saldoByRowId && saldoByRowId[r.id] != null ? saldoByRowId[r.id] : null,
          type: "currency",
          decimals: 2,
        }]
      : []),
  ];

  const filenameBase = `prima_nota_${filters.date_from}_${filters.date_to}`;

  const exportCsv = () => {
    exportRows({ rows, columns, sheetName: "Prima Nota", filenameBase, format: "csv" });
    toast.success("Esportazione CSV pronta.");
  };
  const exportXlsx = () => {
    exportRows({ rows, columns, sheetName: "Prima Nota", filenameBase, format: "xlsx" });
    toast.success("Esportazione Excel pronta.");
  };
  const exportPdf = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      // Header
      doc.setFillColor(42, 75, 60);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 22, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Prima Nota", 14, 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        `Periodo: ${fmtDate(filters.date_from)} — ${fmtDate(filters.date_to)}`,
        14,
        16,
      );
      doc.setFontSize(8);
      doc.text(
        `Generato il ${new Date().toLocaleString("it-IT")}`,
        doc.internal.pageSize.getWidth() - 14,
        16,
        { align: "right" },
      );

      // KPI box
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(9);
      const kpiY = 30;
      doc.text(`Totale Entrate: ${EUR(summary.total_in)}`, 14, kpiY);
      doc.text(`Totale Uscite: ${EUR(summary.total_out)}`, 90, kpiY);
      doc.text(`Saldo Netto: ${EUR(summary.net)}`, 165, kpiY);
      doc.text(`N° Movimenti: ${summary.count}`, 230, kpiY);

      // Table
      const body = rows.map((r) => {
        const docType = documentTypesById[r.doc_type_id];
        const base = [
          fmtDate(r.date),
          r.type === "in" ? "Entrata" : "Uscita",
          r.description || "",
          r.notes || "",
          r.doc_number || r.invoice_reference || "",
          docType?.name || "",
          r.doc_protocol || "",
          r.doc_date ? fmtDate(r.doc_date) : "",
          r.doc_notes || "",
          r.invoice_date ? fmtDate(r.invoice_date) : "",
          r.category_name || "",
          r.subject_name || "",
          r.amount_in != null ? EUR(r.amount_in) : "",
          r.amount_out != null ? EUR(r.amount_out) : "",
          r.method ? paymentMethodIT[r.method] || r.method : "",
          ORIGIN_LABEL[r.origin] || r.origin,
        ];
        if (showSaldo) {
          const s = saldoByRowId ? saldoByRowId[r.id] : null;
          base.push(s != null ? EUR(s) : "");
        }
        return base;
      });
      autoTable(doc, {
        startY: 36,
        head: [[
          "Data", "Tipo", "Descrizione", "Note", "Documento", "Tipo doc.", "Protocollo", "Data doc.", "Note doc.", "Data fatt.",
          "Categoria", "Soggetto",
          "Entrata", "Uscita", "Metodo", "Origine",
          ...(showSaldo ? ["Saldo"] : []),
        ]],
        body,
        styles: { fontSize: 7, cellPadding: 1.2, overflow: "linebreak" },
        headStyles: { fillColor: [42, 75, 60], textColor: 255, fontStyle: "bold" },
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: 14 },
          3: { cellWidth: 30 },
          4: { cellWidth: 24 },
          5: { cellWidth: 18 },
          8: { cellWidth: 20, halign: "right" },
          9: { cellWidth: 20, halign: "right" },
        },
        didDrawPage: () => {
          const pageNo = doc.internal.getNumberOfPages();
          doc.setFontSize(7);
          doc.setTextColor(150);
          doc.text(
            `Pagina ${pageNo}`,
            doc.internal.pageSize.getWidth() - 14,
            doc.internal.pageSize.getHeight() - 6,
            { align: "right" },
          );
        },
      });

      doc.save(`${filenameBase}.pdf`);
      toast.success("Esportazione PDF pronta.");
    } catch (e) {
      toast.error("Errore generazione PDF: " + (e?.message || "sconosciuto"));
    }
  };

  return (
    <div data-testid="prima-nota-page">
      <PageHeader
        title="Prima Nota"
        subtitle="Consuntivo di tutti i movimenti reali registrati — pagamenti e incassi effettivi (non scadenze pianificate)."
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="btn-export"
                disabled={rows.length === 0}
              >
                <Download size={14} className="mr-1.5" />
                Esporta
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={exportCsv}
                data-testid="btn-export-csv"
              >
                <FileText size={14} className="mr-2" /> CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={exportXlsx}
                data-testid="btn-export-xlsx"
              >
                <FileSpreadsheet size={14} className="mr-2" /> Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={exportPdf}
                data-testid="btn-export-pdf"
              >
                <FileText size={14} className="mr-2" /> PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {/* KPI strip */}
      <div data-testid="kpi-strip" className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Totale Entrate"
          value={summary.total_in}
          tone="emerald"
          testId="kpi-in"
          sub={`${summary.count_in || 0} movimenti`}
          onClick={() => setF("tipo", "in")}
          active={filters.tipo === "in"}
          actionHint="Filtra entrate"
        />
        <KpiCard
          label="Totale Uscite"
          value={summary.total_out}
          tone="rose"
          testId="kpi-out"
          sub={`${summary.count_out || 0} movimenti`}
          onClick={() => setF("tipo", "out")}
          active={filters.tipo === "out"}
          actionHint="Filtra uscite"
        />
        <KpiCard
          label="Saldo Netto"
          value={summary.net}
          tone={summary.net >= 0 ? "emerald" : "rose"}
          testId="kpi-net"
          sub="Entrate − Uscite"
          showSign
        />
        <KpiCard
          label="N° Movimenti"
          value={summary.count}
          tone="stone"
          testId="kpi-count"
          plain
          onClick={() => setF("tipo", "all")}
          active={filters.tipo === "all"}
          actionHint="Mostra tutti i movimenti"
        />
      </div>

      {/* Banner: filtro per scadenza specifica (deep-link da Scadenze) */}
      {occurrenceFilter && (
        <div
          data-testid="occurrence-filter-banner"
          className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs"
        >
          <span className="text-stone-700">
            Vista filtrata sui <strong className="text-primary">movimenti reali</strong> di una specifica
            scadenza{rows.length > 0 ? ` · ${rows.length} ${rows.length === 1 ? "movimento" : "movimenti"}` : ""}.
          </span>
          <button
            type="button"
            data-testid="clear-occurrence-filter"
            onClick={() => setOccurrenceFilter("")}
            className="rounded-md border border-primary/40 bg-white px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10"
          >
            Rimuovi filtro scadenza
          </button>
        </div>
      )}

      {/* Saldo progressivo (toggle + saldo iniziale) */}
      <div
        data-testid="saldo-panel"
        className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs"
      >
        <label className="flex cursor-pointer items-center gap-2 text-stone-700">
          <input
            type="checkbox"
            data-testid="toggle-saldo"
            checked={showSaldo}
            onChange={(e) => setShowSaldo(e.target.checked)}
            className="h-4 w-4"
          />
          <span className="font-medium">Mostra saldo progressivo</span>
        </label>
        {showSaldo && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-[11px] text-stone-500">Saldo iniziale</Label>
              <Input
                type="number"
                step="0.01"
                data-testid="saldo-initial"
                value={saldoInitial}
                onChange={(e) => setSaldoInitial(e.target.value)}
                placeholder="0,00"
                className="h-7 w-32 text-right font-mono text-xs tabular-nums"
              />
              <span className="text-[11px] text-stone-400">
                EUR all&apos;inizio del periodo
              </span>
            </div>
            <span className="ml-auto text-[11px] text-stone-500">
              Calcolato cronologicamente. Il valore di ogni riga è il saldo
              cumulato al termine di quel movimento.
            </span>
          </>
        )}
      </div>

      {/* Filters */}
      <div
        data-testid="filters-bar"
        className="mb-4 rounded-xl border border-stone-200 bg-white p-4"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
            <Filter size={12} /> Filtri
          </div>
          <PeriodPicker
            value={{ from: filters.date_from, to: filters.date_to }}
            onChange={(from, to) =>
              setFilters((s) => ({ ...s, date_from: from, date_to: to }))
            }
            testId="pn-period"
            align="end"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-[11px]">Da</Label>
            <Input
              type="date"
              data-testid="filter-date-from"
              value={filters.date_from}
              onChange={(e) => setF("date_from", e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div>
            <Label className="text-[11px]">A</Label>
            <Input
              type="date"
              data-testid="filter-date-to"
              value={filters.date_to}
              onChange={(e) => setF("date_to", e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div>
            <Label className="text-[11px]">Tipo movimento</Label>
            <Select value={filters.tipo} onValueChange={(v) => setF("tipo", v)}>
              <SelectTrigger data-testid="filter-tipo" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="in">Solo entrate</SelectItem>
                <SelectItem value="out">Solo uscite</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Origine</Label>
            <Select value={filters.origin} onValueChange={(v) => setF("origin", v)}>
              <SelectTrigger data-testid="filter-origin" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {Object.entries(ORIGIN_LABEL).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Categoria</Label>
            <Select
              value={filters.category_id}
              onValueChange={(v) => setF("category_id", v)}
            >
              <SelectTrigger data-testid="filter-category" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {categoryOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Soggetto (cliente/fornitore)</Label>
            <Select
              value={filters.subject_id}
              onValueChange={(v) => setF("subject_id", v)}
            >
              <SelectTrigger data-testid="filter-subject" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {subjectOptions.map((s) => (
                  <SelectItem key={`${s.kind}-${s.id}`} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Metodo</Label>
            <Select value={filters.method} onValueChange={(v) => setF("method", v)}>
              <SelectTrigger data-testid="filter-method" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {METHOD_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Ricerca testuale</Label>
            <Input
              type="text"
              data-testid="filter-q"
              placeholder="Descrizione, note, riferimento…"
              value={filters.q}
              onChange={(e) => setF("q", e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-stone-100 pt-3">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-stone-700">
            <input
              type="checkbox"
              data-testid="filter-partial-only"
              checked={filters.partial_only}
              onChange={(e) => setF("partial_only", e.target.checked)}
              className="h-4 w-4"
            />
            Solo movimenti parziali
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="btn-reset-filters"
            onClick={() => {
              setFilters({
                date_from: yFrom,
                date_to: yTo,
                tipo: "all",
                category_id: "all",
                subject_id: "all",
                method: "all",
                origin: "all",
                q: "",
                partial_only: false,
              });
            }}
            className="text-[11px]"
          >
            Reset filtri
          </Button>
          <TagFilter
            selectedIds={tagFilter.selectedIds}
            mode={tagFilter.mode}
            untaggedOnly={tagFilter.untaggedOnly}
            onChange={setTagFilter}
            testIdPrefix="prima-nota-tag-filter"
          />
          {/* v1.59 ck14.2: filtro Tipo documento + presenza doc */}
          <Select
            value={filters.doc_type_id || "all"}
            onValueChange={(v) => setF("doc_type_id", v === "all" ? null : v)}
          >
            <SelectTrigger
              className="h-7 w-[150px] text-[11px]"
              data-testid="filter-doc-type"
            >
              <SelectValue placeholder="Tipo documento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi doc.</SelectItem>
              {documentTypes.filter((t) => t.is_active).map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm" style={{ background: t.color }} />
                    {t.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.doc_presence || "any"}
            onValueChange={(v) => setF("doc_presence", v === "any" ? null : v)}
          >
            <SelectTrigger
              className="h-7 w-[140px] text-[11px]"
              data-testid="filter-doc-presence"
            >
              <SelectValue placeholder="Documento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Doc: tutti</SelectItem>
              <SelectItem value="with">Con documento</SelectItem>
              <SelectItem value="without">Senza documento</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active filter chips — drill-down feedback */}
      <ActiveFilterBar
        filters={filters}
        setF={setF}
        categories={[...categoriesAp, ...categoriesAr]}
        subjects={[
          ...vendors.map((v) => ({ id: v.id, name: v.name, kind: "vendor" })),
          ...clients.map((c) => ({ id: c.id, name: c.name, kind: "client" })),
        ]}
        onResetAll={() =>
          setFilters({
            date_from: yFrom,
            date_to: yTo,
            tipo: "all",
            category_id: "all",
            subject_id: "all",
            method: "all",
            origin: "all",
            q: "",
            partial_only: false,
          })
        }
      />

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
        <table className="w-full text-sm" data-testid="prima-nota-table">
          <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              <Th onClick={() => toggleSort("date")} testId="th-date">
                Data {sortIcon("date")}
              </Th>
              <Th onClick={() => toggleSort("type")} testId="th-type">
                Tipo {sortIcon("type")}
              </Th>
              <Th testId="th-description">Descrizione</Th>
              <Th testId="th-notes">Note</Th>
              <Th testId="th-invoice-ref">Documento</Th>
              <Th testId="th-invoice-date">Data fattura</Th>
              <Th testId="th-category">Categoria</Th>
              <Th onClick={() => toggleSort("subject")} testId="th-subject">
                Soggetto {sortIcon("subject")}
              </Th>
              <Th align="right" onClick={() => toggleSort("amount")} testId="th-amount-in">
                Entrata {sortIcon("amount")}
              </Th>
              <Th align="right" testId="th-amount-out">Uscita</Th>
              <Th testId="th-method">Metodo</Th>
              <Th onClick={() => toggleSort("origin")} testId="th-origin">
                Origine {sortIcon("origin")}
              </Th>
              {showSaldo && (
                <Th align="right" testId="th-saldo">Saldo</Th>
              )}
            </tr>
          </thead>
          <tbody data-testid="prima-nota-tbody">
            {loading ? (
              <tr>
                <td colSpan={showSaldo ? 13 : 12} className="px-3 py-10 text-center text-xs text-stone-400">
                  Caricamento…
                </td>
              </tr>
            ) : rowsFiltered.length === 0 ? (
              <tr>
                <td
                  colSpan={showSaldo ? 13 : 12}
                  data-testid="prima-nota-empty"
                  className="px-3 py-10 text-center text-xs text-stone-400"
                >
                  Nessun movimento registrato nel periodo / con i filtri impostati.
                </td>
              </tr>
            ) : (
              rowsFiltered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`row-${r.id}`}
                  className="border-t border-stone-100 hover:bg-stone-50/50"
                >
                  <td className="px-3 py-2 font-mono text-xs tabular-nums text-stone-700">
                    {fmtDate(r.date)}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`row-tipo-${r.id}`}
                      onClick={() => setF("tipo", r.type)}
                      title={`Filtra per ${r.type === "in" ? "Entrate" : "Uscite"}`}
                      className={
                        "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-all hover:scale-105 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 " +
                        (r.type === "in"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200 focus:ring-emerald-400"
                          : "bg-rose-50 text-rose-700 border border-rose-200 focus:ring-rose-400")
                      }
                    >
                      {r.type === "in" ? "Entrata" : "Uscita"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-stone-800">
                    <div className="font-medium">{r.description || "—"}</div>
                    {r.is_partial && (
                      <span
                        data-testid={`partial-${r.id}`}
                        className="mt-0.5 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-800 border border-amber-200"
                      >
                        Parziale
                      </span>
                    )}
                    {/* Fase 2 etichette: chip inline + popover */}
                    {(() => {
                      const ent = resolveByRowKey(r.id);
                      if (!ent) return null;
                      const rowTags = tagsByEntity[r.id] || [];
                      return (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <TagList
                            tags={rowTags}
                            size="sm"
                            max={3}
                            onRemove={async (t) => {
                              try {
                                await (await import("@/lib/api")).detachTag(
                                  t.id,
                                  ent.entityType,
                                  ent.entityId,
                                );
                                refetchTags();
                              } catch {
                                /* silent */
                              }
                            }}
                          />
                          <TagAssignmentPopover
                            entityType={ent.entityType}
                            entityId={ent.entityId}
                            currentTagIds={rowTags.map((t) => t.id)}
                            onChange={() => refetchTags()}
                            testIdPrefix={`prima-nota-tag-${r.id}`}
                          />
                        </div>
                      );
                    })()}
                  </td>
                  <td
                    className="px-3 py-2 text-xs text-stone-700 max-w-[220px]"
                    data-testid={`row-notes-${r.id}`}
                  >
                    {r.notes ? (
                      <div
                        className="truncate"
                        title={r.notes}
                      >
                        {r.notes}
                      </div>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td
                    className="px-3 py-2 text-xs text-stone-700 max-w-[220px]"
                    data-testid={`row-invoice-ref-${r.id}`}
                  >
                    {(() => {
                      // v1.59 ck14.2: preferisci Riferimento Documento (badge);
                      // fallback su invoice_reference legacy.
                      const docType = documentTypesById[r.doc_type_id];
                      if (r.doc_type_id || r.doc_number) {
                        return (
                          <DocumentBadge
                            type={docType}
                            number={r.doc_number || r.invoice_reference}
                            date={r.doc_date}
                            attachedCount={attachmentsCountByRow[r.id] || 0}
                            testId={`doc-badge-${r.id}`}
                          />
                        );
                      }
                      if (r.invoice_reference) {
                        return (
                          <div className="truncate text-[11px]" title={r.invoice_reference}>
                            {r.invoice_reference}
                          </div>
                        );
                      }
                      return <span className="text-stone-300">—</span>;
                    })()}
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-xs tabular-nums text-stone-600"
                    data-testid={`row-invoice-date-${r.id}`}
                  >
                    {(r.doc_date || r.invoice_date) ? fmtDate(r.doc_date || r.invoice_date) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-stone-600">
                    {r.category_id ? (
                      <button
                        type="button"
                        data-testid={`row-category-${r.id}`}
                        onClick={() => setF("category_id", r.category_id)}
                        title={`Filtra per categoria "${r.category_name}"`}
                        className="rounded px-1 -mx-1 text-left underline-offset-2 hover:bg-primary/5 hover:text-primary hover:underline focus:outline-none focus:ring-1 focus:ring-primary/40"
                      >
                        {r.category_name}
                      </button>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-stone-700">
                    {r.subject_id && r.subject_name ? (
                      <button
                        type="button"
                        data-testid={`row-subject-${r.id}`}
                        onClick={() => setF("subject_id", r.subject_id)}
                        title={`Filtra per soggetto "${r.subject_name}"`}
                        className="rounded px-1 -mx-1 text-left underline-offset-2 hover:bg-primary/5 hover:text-primary hover:underline focus:outline-none focus:ring-1 focus:ring-primary/40"
                      >
                        {r.subject_name}
                      </button>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular-nums text-emerald-700">
                    {r.amount_in != null ? EUR(r.amount_in) : ""}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular-nums text-rose-700">
                    {r.amount_out != null ? EUR(r.amount_out) : ""}
                  </td>
                  <td className="px-3 py-2 text-xs text-stone-600">
                    {r.method ? paymentMethodIT[r.method] || r.method : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`row-origin-${r.id}`}
                      onClick={() => setF("origin", r.origin)}
                      title={`Filtra per origine "${ORIGIN_LABEL[r.origin] || r.origin}"`}
                      className={
                        "inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium transition-all hover:scale-105 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary/40 " +
                        (ORIGIN_TONE[r.origin] || "bg-stone-50 text-stone-600 border-stone-200")
                      }
                    >
                      {ORIGIN_LABEL[r.origin] || r.origin}
                    </button>
                  </td>
                  {showSaldo && (
                    <td
                      data-testid={`row-saldo-${r.id}`}
                      className={
                        "px-3 py-2 text-right font-mono text-xs tabular-nums " +
                        (saldoByRowId && saldoByRowId[r.id] != null
                          ? saldoByRowId[r.id] >= 0
                            ? "text-emerald-700"
                            : "text-rose-700 font-semibold"
                          : "text-stone-400")
                      }
                    >
                      {saldoByRowId && saldoByRowId[r.id] != null
                        ? EUR(saldoByRowId[r.id])
                        : ""}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {rowsFiltered.length > 0 && (
        <div
          data-testid="footer-totals"
          className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 bg-stone-50/60 px-4 py-2 text-xs text-stone-600"
        >
          <span>
            {tagFilter.untaggedOnly
              ? `${rowsFiltered.length} di ${rows.length} movimenti · solo senza etichetta`
              : tagFilter.selectedIds.length > 0
                ? `${rowsFiltered.length} di ${rows.length} movimenti · filtro etichette attivo`
                : `${rows.length} movimenti visualizzati`}
            {" · Periodo "}
            {fmtDate(filters.date_from)} — {fmtDate(filters.date_to)}
          </span>
          <span className="font-mono tabular-nums">
            <span className="text-emerald-700">+ {EUR(summary.total_in)}</span>
            <span className="mx-2 text-stone-400">/</span>
            <span className="text-rose-700">− {EUR(summary.total_out)}</span>
            <span className="mx-2 text-stone-400">=</span>
            <strong className={summary.net >= 0 ? "text-emerald-700" : "text-rose-700"}>
              {summary.net >= 0 ? "+" : ""}{EUR(summary.net)}
            </strong>
          </span>
        </div>
      )}
    </div>
  );
}

function Th({ children, onClick, align = "left", testId }) {
  return (
    <th
      data-testid={testId}
      className={
        "px-3 py-2 select-none " +
        (align === "right" ? "text-right" : "text-left") +
        (onClick ? " cursor-pointer hover:bg-stone-100" : "")
      }
      onClick={onClick}
    >
      {children}
    </th>
  );
}

function KpiCard({ label, value, tone = "stone", sub, testId, showSign, plain, onClick, active, actionHint }) {
  const colorMap = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    stone: "text-stone-900",
  };
  const formatted = plain
    ? value
    : showSign
      ? `${value >= 0 ? "+" : ""}${EUR(value)}`
      : EUR(value);
  const baseCls = "min-w-0 rounded-xl border bg-white p-4 text-left transition-all";
  const interactiveCls = onClick
    ? " cursor-pointer hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-primary/40"
    : "";
  const activeCls = active
    ? " border-primary ring-1 ring-primary/30"
    : " border-stone-200";
  const content = (
    <>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
        {active && (
          <span
            data-testid={`${testId}-active`}
            className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary"
          >
            Filtro attivo
          </span>
        )}
      </div>
      <div
        className={`mt-1 truncate font-mono text-xl font-semibold tabular-nums ${colorMap[tone]}`}
        title={typeof formatted === "string" ? formatted : undefined}
      >
        {formatted}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-stone-500">{sub}</div>}
      {onClick && actionHint && !active && (
        <div className="mt-1 text-[10px] italic text-stone-400">{actionHint} →</div>
      )}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        title={actionHint}
        className={baseCls + interactiveCls + activeCls + " w-full"}
      >
        {content}
      </button>
    );
  }
  return (
    <div data-testid={testId} className={baseCls + activeCls}>
      {content}
    </div>
  );
}

const TIPO_LABEL = { in: "Entrata", out: "Uscita" };

const METHOD_LABEL_MAP = METHOD_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {},
);

function ActiveFilterBar({ filters, setF, categories, subjects, onResetAll }) {
  const chips = [];
  if (filters.tipo && filters.tipo !== "all") {
    chips.push({
      key: "tipo",
      label: "Tipo",
      value: TIPO_LABEL[filters.tipo] || filters.tipo,
      onClear: () => setF("tipo", "all"),
    });
  }
  if (filters.origin && filters.origin !== "all") {
    chips.push({
      key: "origin",
      label: "Origine",
      value: ORIGIN_LABEL[filters.origin] || filters.origin,
      onClear: () => setF("origin", "all"),
    });
  }
  if (filters.category_id && filters.category_id !== "all") {
    const cat = categories.find((c) => c.id === filters.category_id);
    chips.push({
      key: "category",
      label: "Categoria",
      value: cat ? cat.name : filters.category_id,
      onClear: () => setF("category_id", "all"),
    });
  }
  if (filters.subject_id && filters.subject_id !== "all") {
    const sub = subjects.find((s) => s.id === filters.subject_id);
    chips.push({
      key: "subject",
      label: "Soggetto",
      value: sub ? sub.name : filters.subject_id,
      onClear: () => setF("subject_id", "all"),
    });
  }
  if (filters.method && filters.method !== "all") {
    chips.push({
      key: "method",
      label: "Metodo",
      value: METHOD_LABEL_MAP[filters.method] || filters.method,
      onClear: () => setF("method", "all"),
    });
  }
  if (filters.q && filters.q.trim() !== "") {
    chips.push({
      key: "q",
      label: "Ricerca",
      value: `"${filters.q}"`,
      onClear: () => setF("q", ""),
    });
  }
  if (filters.partial_only) {
    chips.push({
      key: "partial",
      label: "Stato",
      value: "Solo parziali",
      onClear: () => setF("partial_only", false),
    });
  }
  if (chips.length === 0) return null;

  return (
    <div
      data-testid="active-filters-bar"
      className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs"
    >
      <span className="font-semibold uppercase tracking-wider text-primary">
        Filtri attivi
      </span>
      {chips.map((c) => (
        <span
          key={c.key}
          data-testid={`active-chip-${c.key}`}
          className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-white px-2 py-0.5 text-[11px] text-stone-700"
        >
          <span className="text-stone-500">{c.label}:</span>
          <strong className="font-medium text-stone-900">{c.value}</strong>
          <button
            type="button"
            data-testid={`active-chip-clear-${c.key}`}
            onClick={c.onClear}
            aria-label={`Rimuovi filtro ${c.label}`}
            className="ml-0.5 rounded-full px-1 text-stone-400 hover:bg-rose-50 hover:text-rose-700"
          >
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        data-testid="active-clear-all"
        onClick={onResetAll}
        className="ml-auto text-[11px] font-medium text-primary underline-offset-2 hover:underline"
      >
        Rimuovi tutti i filtri
      </button>
    </div>
  );
}

